import React, { useEffect, useRef, useState } from 'react';
import { jsonEditorStyles } from '../jsonEditorStyles';
import Button from '@mui/material/Button';
import cn from 'clsx';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import useComponentUIHook from './useComponentUIHook';
import { SelectComponent, TextComponent, TooltipComponent } from '../../uiComponents';
import { SelectChangeEvent, Typography } from '@mui/material';
import { OptionProps } from '../../../view-models';
import { cloneDeep } from 'lodash';
import SessionManagement, { sessionFieldsAreValid } from './sessionManagement';
import jsonComponents from '../../../constants/jsonComponents';
import { FieldMapperRow } from './fieldMapperRow';
import { AnyTxtRecord } from 'dns';
import TemplatizedFieldComponent, { getTemplatizedValue, TemplatizedValue } from './templatizedFieldComponent';

interface ComponentProps {
    componentData: any;
    closeGritComponent: any;
    setUnsavedChanges: any;
}

export const GenerateSAMLIdPUI = ({componentData, closeGritComponent, setUnsavedChanges}: ComponentProps) => {
    const { modifiedData, setModifiedData, checkIfSameIgnoringSubFlows, allClaims, modifiedTemplatizations, setModifiedTemplatizations } = useComponentUIHook(componentData);
    const [claimsList, setClaimsList] = useState<OptionProps[]>([]);
    const [nextOutputClaim, setNextOutputClaim] = useState({
        claimId: '',
        partnerClaimType: '',
        defaultValue: '',
        alwaysUseDefaultValue: false
    });
    const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
    const configData = jsonComponents.samlIdP.data;
    const classes = jsonEditorStyles();

    useEffect(() => {
        if (Object.keys(allClaims).length > 0) {
          const _temp = Object.keys(allClaims)
            .reduce((acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }), [])
            .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1));
          setClaimsList(_temp);
        }
    }, [allClaims]);

    useEffect(() => {
        const _res: boolean = checkIfSameIgnoringSubFlows();
        setDisableSubmit(
            _res 
            || (!modifiedData['issuerIdPartnerClaimType'])
            || (!modifiedData['metadataUrl'])
            || (!modifiedData['idpDomain'])
            || ((modifiedData['signRequests']) && ((!modifiedData['messageSigningKey']) || (!modifiedData['signatureAlgorithm'])))
            || ((modifiedData['assertionsEncrypted'] && (!modifiedData['assertionDecryptionKey'])))
            || ((modifiedData['requestUser']) && (!modifiedData['requestUserClaim']))
            || (!sessionFieldsAreValid(modifiedData))
        );
        setUnsavedChanges(_res);
    }, [modifiedData]);

    return (
        <div>
            <div className={cn(classes.flexRow)}>
                <TemplatizedFieldComponent
                    changeHandler={(e: TemplatizedValue) => {
                        setModifiedData({
                            ...modifiedData, idpDomain: { ...e }
                        });
                    }}
                    modifiedTemplatizations={modifiedTemplatizations}
                    setModifiedTemplatizations={setModifiedTemplatizations}
                    data={{
                        fieldPath: 'idpDomain',
                        label: 'Identity Provider Domain',
                        required: true,
                        value: getTemplatizedValue(modifiedData['idpDomain']),
                        style: { margin: 1, width: 700 },
                        variant: 'text'
                    }}
                />
                <TooltipComponent
                    title="Identity Provider Domain"
                    desc="The domain ID of the identity provider (such as 'google.com'). SAML IdP Authentication components with the same IdP domain will be assumed to refer to the same IdP. This domain may also be matched to a domain hint parameter."
                />
            </div>
            <div className={cn(classes.flexRow)}>
                <TemplatizedFieldComponent
                    changeHandler={(e: TemplatizedValue) => {
                        setModifiedData({
                            ...modifiedData, metadataUrl: { ...e }
                        });
                    }}
                    modifiedTemplatizations={modifiedTemplatizations}
                    setModifiedTemplatizations={setModifiedTemplatizations}
                    data={{
                        fieldPath: 'metadataUrl',
                        label: 'SAML IdP Metadata URL',
                        required: true,
                        value: getTemplatizedValue(modifiedData['metadataUrl']),
                        style: { margin: 1, width: 700 },
                        variant: 'text'
                    }}
                />
                <TooltipComponent
                    title="SAML IdP Metadata URL"
                    desc="The URL of the identity provider's SAML metadata endpoint."
                />
            </div>
            <div className={cn(classes.flexRow)}>
                <FormControlLabel
                    sx={{ m: 0, width: '300px' }}
                    label={'Sign Requests to Provider'}
                    control={
                        <Checkbox
                            checked={modifiedData['signRequests']}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setModifiedData({
                                    ...modifiedData,
                                    signRequests: !modifiedData['signRequests'],
                                });
                            }}
                        />
                    }
                />
                <TooltipComponent
                    title="Sign Requests to Provider"
                    desc="Check this if the SAML IdP expects signed requests. If enabled, a Message Signing Key Container must be specified."
                />
            </div>
            {modifiedData['signRequests'] && (
                <div className={cn(classes.flexRow)} style={{marginLeft: '20px'}}>
                    <SelectComponent
                        changeHandler={(e: SelectChangeEvent<string>) => {
                            setModifiedData({
                                ...modifiedData,
                                signatureAlgorithm: e.target.value
                            })
                        }}
                        data={{
                            value: modifiedData['signatureAlgorithm'] ?? '',
                            label: 'Request Signature Algorithm',
                            options: [
                                { value: 'Sha1', label: 'SHA-1' },
                                { value: 'Sha256', label: 'SHA-256' },
                                { value: 'Sha384', label: 'SHA-384' },
                                { value: 'Sha512', label: 'SHA-512' }
                            ],
                            required: modifiedData['signRequests'],
                            style: { margin: 1, width: 500 }
                        }}
                    />
                </div>
            )}
            <div className={cn(classes.flexRow)}>
                <FormControlLabel
                    sx={{ m: 0, width: '300px' }}
                    label={'Validate Response Signatures'}
                    control={
                        <Checkbox
                            checked={modifiedData['responseSignatures']}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setModifiedData({
                                    ...modifiedData,
                                    responseSignatures: !modifiedData['responseSignatures'],
                                });
                            }}
                        />
                    }
                />
                <TooltipComponent
                    title="Validate Response Signatures"
                    desc="Require responses and assertions obtained from the SAML IdP to have valid signatures."
                />
            </div>
            <div className={cn(classes.flexRow)}>
                <FormControlLabel
                    sx={{ m: 0, width: '300px' }}
                    label={'Assertions are Encrypted'}
                    control={
                        <Checkbox
                            checked={modifiedData['assertionsEncrypted']}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setModifiedData({
                                    ...modifiedData,
                                    assertionsEncrypted: !modifiedData['assertionsEncrypted'],
                                });
                            }}
                        />
                    }
                />
                <TooltipComponent
                    title="Assertions are Encrypted"
                    desc="Require assertions obtained from the SAML IdP to be encrypted. If enabled, an Assertion Decryption Key Container must be specified."
                />
            </div>
            <div className={cn(classes.flexRow)}>
                <FormControlLabel
                    sx={{ m: 0, width: '300px' }}
                    label={'Specify User in Request'}
                    control={
                        <Checkbox
                            checked={modifiedData['requestUser']}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setModifiedData({
                                    ...modifiedData,
                                    requestUser: !modifiedData['requestUser'],
                                });
                            }}
                        />
                    }
                />
                <TooltipComponent
                    title="Specify User in Request"
                    desc="Check if a user ID (issued by the IdP) should be sent to the provider as part of the SAML request."
                />
            </div>
            {modifiedData['requestUser'] && (
                <div className={cn(classes.flexRow)}>
                    <SelectComponent
                        changeHandler={(e: SelectChangeEvent<string>) => {
                            setModifiedData({
                                ...modifiedData,
                                requestUserClaim: e.target.value
                            })
                        }}
                        data={{
                            value: modifiedData['requestUserClaim'] ?? '',
                            label: 'Request User Claim',
                            options: claimsList,
                            required: modifiedData['requestUser'],
                            style: { margin: 1, width: 500 }
                        }}
                    />
                    <TooltipComponent
                        title="Request User Claim"
                        desc="The claim from which to populate the issuer user ID sent to the SAML IdP as part of the request."
                    />
                </div>
            )}
            <div className={cn(classes.flexRow)}>
                <TextComponent
                    changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                        setModifiedData({
                            ...modifiedData,
                            issuerIdPartnerClaimType: e.target.value
                        });
                    }}
                    data={{
                        label: 'Issuer User ID',
                        value: modifiedData['issuerIdPartnerClaimType'] ?? '',
                        required: true,
                        style: { margin: 1, width: 500 }
                    }}
                />
                <TooltipComponent
                    title="Issuer User ID"
                    desc="If the SAML NameID assertion from the IdP will contain an SPNameQualifier attribute, enter the value of that attribute here. If it will not present this attribute, but will present the NameQualifier attribute, enter the value of the NameQualifier attribute. If neither attribute will be presented, enter 'assertionSubjectName' here."
                />
            </div>
            <div className={cn(classes.flexRow)}>
                <Typography>
                    Cryptographic Key Containers
                </Typography>
                <TooltipComponent
                    title="Cryptographic Key Container"
                    desc="Identifiers for containers on the ADB2C tenant storing cryptographic keys that will be used by this component."
                />
            </div>
            <div style={{marginLeft: '20px'}}>
                <div className={cn(classes.flexRow)}>
                    <TemplatizedFieldComponent
                        changeHandler={(e: TemplatizedValue) => {
                            setModifiedData({
                                ...modifiedData, messageSigningKey: { ...e }
                            });
                        }}
                        modifiedTemplatizations={modifiedTemplatizations}
                        setModifiedTemplatizations={setModifiedTemplatizations}
                        data={{
                            fieldPath: 'messageSigningKey',
                            label: 'Message Signing Key Container',
                            required: modifiedData['signRequests'] ?? false,
                            value: getTemplatizedValue(modifiedData['messageSigningKey']),
                            style: { margin: 1, width: 700 },
                            variant: 'text'
                        }}
                    />
                    <TooltipComponent
                        title="Message Signing Key"
                        desc="The container ID storing the X509 certificate used to sign SAML messages sent to the IdP by ADB2C."
                    />
                </div>
                <div className={cn(classes.flexRow)}>
                    <TemplatizedFieldComponent
                        changeHandler={(e: TemplatizedValue) => {
                            setModifiedData({
                                ...modifiedData, assertionDecryptionKey: { ...e }
                            });
                        }}
                        modifiedTemplatizations={modifiedTemplatizations}
                        setModifiedTemplatizations={setModifiedTemplatizations}
                        data={{
                            fieldPath: 'assertionDecryptionKey',
                            label: 'Assertion Decryption Key Container',
                            required: modifiedData['assertionsEncrypted'] ?? false,
                            value: getTemplatizedValue(modifiedData['assertionDecryptionKey']),
                            style: { margin: 1, width: 700 },
                            variant: 'text'
                        }}
                    />
                    <TooltipComponent
                        title="Assertion Decryption Key"
                        desc="The container ID storing the X509 certificate used to provide public key data to the SAML IdP for assertion encryption and from which the private key will be used by ADB2C to decrypt the assertions."
                    />
                </div>
                <div className={cn(classes.flexRow)}>
                    <TemplatizedFieldComponent
                        changeHandler={(e: TemplatizedValue) => {
                            setModifiedData({
                                ...modifiedData, metadataSigningKey: { ...e }
                            });
                        }}
                        modifiedTemplatizations={modifiedTemplatizations}
                        setModifiedTemplatizations={setModifiedTemplatizations}
                        data={{
                            fieldPath: 'metadataSigningKey',
                            label: 'Metadata Signing Key Container',
                            value: getTemplatizedValue(modifiedData['metadataSigningKey']),
                            style: { margin: 1, width: 700 },
                            variant: 'text'
                        }}
                    />
                    <TooltipComponent
                        title="Metadata Signing Key"
                        desc="The container ID storing the X509 certificate used by ADB2C to sign the SAML metadata provided for this component."
                    />
                </div>
            </div>
            <Typography>
                Output Claims
            </Typography>
            <div style={{marginLeft: '20px'}}>
                {modifiedData['outputClaims'].map((ocRef: any, ocIdx: number) => {
                    return (
                        <FieldMapperRow
                            key={ocIdx}
                            rowData={ocRef}
                            writeback={(rowData) => {
                                const _outClaims = modifiedData['outputClaims'].slice();
                                _outClaims[ocIdx] = rowData;
                                setModifiedData({
                                    ...modifiedData,
                                    outputClaims: _outClaims
                                });
                            }}
                            claimsList={claimsList.reduce((opts: OptionProps[], nxt: OptionProps) => {
                                if (!modifiedData['outputClaims'].find((oc: any) => { return oc.claimId === nxt.value })) {
                                  opts.push(nxt);
                                }
                                return opts;
                            }, [{ value: ocRef.claimId, label: ocRef.claimId }])}
                            allClaims={allClaims}
                            enabledSources={['claim']}
                            fixedSourceType='claim'
                            sourceFieldName='claimId'
                            sourceFieldLabels={{claim: 'Claim'}}
                            partnerTypeFieldName='partnerClaimType'
                            partnerTypeFieldLabel='Partner Claim Type'
                            defaultValueFieldLabel='Default Value'
                            defaultValueFieldName='defaultValue'
                            allowEmptyDefaultValue={true}
                            alwaysUseDefaultValueFieldName='alwaysUseDefaultValue'
                            buttonText='Delete'
                            buttonAction={() => {
                                const _outClaims = modifiedData['outputClaims'].filter((cl: any, clIdx: number) => { return clIdx !== ocIdx });
                                setModifiedData({
                                    ...modifiedData,
                                    outputClaims: _outClaims
                                });
                            }}
                        />
                    )
                })}
                <FieldMapperRow
                    rowData={nextOutputClaim}
                    writeback={(rowData) => {
                        setNextOutputClaim(rowData);
                    }}
                    claimsList={claimsList.reduce((opts: OptionProps[], nxt: OptionProps) => {
                        if (!modifiedData['outputClaims'].find((oc: any) => { return oc.claimId === nxt.value })) {
                          opts.push(nxt);
                        }
                        return opts;
                    }, [])}
                    allClaims={allClaims}
                    enabledSources={['claim']}
                    fixedSourceType='claim'
                    sourceFieldName='claimId'
                    sourceFieldLabels={{claim: 'Claim'}}
                    partnerTypeFieldName='partnerClaimType'
                    partnerTypeFieldLabel='Partner Claim Type'
                    defaultValueFieldLabel='Default Value'
                    defaultValueFieldName='defaultValue'
                    allowEmptyDefaultValue={true}
                    alwaysUseDefaultValueFieldName='alwaysUseDefaultValue'
                    buttonText='Add'
                    buttonAction={() => {
                        const _outClaims = modifiedData['outputClaims'].slice();
                        _outClaims.push({ claimId: nextOutputClaim.claimId, partnerClaimType: nextOutputClaim.partnerClaimType, defaultValue: nextOutputClaim.defaultValue, alwaysUseDefaultValue: nextOutputClaim.alwaysUseDefaultValue});
                        setModifiedData({
                            ...modifiedData,
                            outputClaims: _outClaims
                        });
                        setNextOutputClaim({
                            claimId: '',
                            partnerClaimType: '',
                            defaultValue: '',
                            alwaysUseDefaultValue: false
                        });
                    }}
                    buttonDisabled={(!nextOutputClaim.claimId) || ((!nextOutputClaim.partnerClaimType) && (!nextOutputClaim.defaultValue))}
                />
            </div>
            {configData.sessionManagement && (
                <SessionManagement
                    modifiedData={modifiedData}
                    changeHandler={(data: any) => setModifiedData(data)}
                    sessionTypes={configData.sessionTypes}
                />
            )}
            <div>
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={disableSubmit}
                    sx={{ mt: 3, mb: 2 }}
                    onClick={() => {
                        closeGritComponent(cloneDeep(modifiedData), modifiedTemplatizations);
                    }}
                >
                    Save
                </Button>
            </div>
        </div>
    );
}